import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Wallet, Landmark, TrendingUp, TrendingDown, Clock, AlertTriangle, CalendarRange, Users,
  ArrowRight, RefreshCw, MapPin, Hand, Banknote, BellRing, FileText
} from 'lucide-react';

const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tl = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tlShort = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
const trDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('tr-TR') : '-');

const KIND_STYLE = {
  organization: 'border-l-[#176B91] bg-sky-50/70',
  reservation: 'border-l-emerald-500 bg-emerald-50/60',
};

const KpiCard = ({ icon: Icon, label, value, sub, tone = 'slate', testid }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700',
    sky: 'bg-sky-50 text-[#176B91]',
    amber: 'bg-amber-50 text-amber-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    slate: 'bg-slate-100 text-slate-700',
  };
  return (
    <Card className="border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`rounded-xl p-2.5 shrink-0 ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
        <div className="min-w-0">
          <p className="text-[12px] text-slate-500 font-medium truncate">{label}</p>
          <p className="text-xl font-bold text-slate-900 tracking-tight" data-testid={testid}>{value}</p>
          {sub && <p className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

const Panel = ({ title, icon: Icon, action, children, testid }) => (
  <Card className="border-[#E2E8F0] shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#176B91]" />}
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      {action}
    </div>
    <CardContent className="p-4">{children}</CardContent>
  </Card>
);

const DashboardHome = ({ currentUser, isPersonnel, canFinance = true, onNavigate }) => {
  const [data, setData] = useState(null);
  const [refereePending, setRefereePending] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [showInvUnpaid, setShowInvUnpaid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ref] = await Promise.all([
        axios.get(`${API}/dashboard/overview`),
        isPersonnel ? Promise.resolve({ data: { referees: [] } }) : axios.get(`${API}/referees/pending-summary`).catch(() => ({ data: { referees: [] } })),
      ]);
      setData(ov.data);
      setRefereePending((ref.data?.referees || []).filter(r => (r.to_collect || 0) > 0).slice(0, 5));
      axios.get(`${API}/reminders`).then(r => setReminders(r.data?.due_today || [])).catch(() => {});
    } catch (e) { /* sessiz — yetki/oturum hatası interceptor tarafından yönetilir */ }
    finally { setLoading(false); }
  }, [isPersonnel]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const k = data?.kpi;
  const showFinance = canFinance && data?.can_see_finance !== false;
  const accountCards = showFinance ? (data?.account_cards || []) : [];
  const financeKpis = k && showFinance ? [
    { label: 'Bu Ay Gelir', value: tl(k.month_income), icon: TrendingUp, tone: 'emerald', sub: k.prev_month_income ? `%${Math.abs(k.income_change)} ${k.income_change >= 0 ? 'artış' : 'azalış'} (geçen aya göre)` : 'geçen ay verisi yok', testid: 'kpi-month-income' },
    { label: 'Bu Ay Gider', value: tl(k.month_expense), icon: TrendingDown, tone: 'rose', sub: k.prev_month_expense ? `%${Math.abs(k.expense_change)} ${k.expense_change >= 0 ? 'artış' : 'azalış'} (geçen aya göre)` : 'geçen ay verisi yok', testid: 'kpi-month-expense' },
    { label: 'Bekleyen Tahsilatlar', value: tl(k.pending_receivable), icon: Clock, tone: 'amber', sub: `${k.debtor_count} borçlu müşteri`, testid: 'kpi-pending-receivable' },
    { label: 'Bekleyen Ödemeler', value: tl(k.pending_payable), icon: AlertTriangle, tone: 'rose', sub: `${k.payable_count} organizasyon gideri`, testid: 'kpi-pending-payable' },
  ] : [];
  const invUnpaid = data?.invoiced_unpaid;
  const kpis = k ? [
    ...financeKpis,
    { label: 'Aktif Organizasyonlar', value: k.active_organizations, icon: CalendarRange, tone: 'indigo', sub: `${k.upcoming_organizations} yaklaşan`, testid: 'kpi-active-organizations' },
    { label: 'Bugünkü Rezervasyonlar', value: k.today_reservations, icon: Users, tone: 'slate', sub: `${k.today_active_salons} salon aktif`, testid: 'kpi-today-reservations' },
  ] : [];

  const chartMax = data && showFinance && data.monthly_chart?.length
    ? Math.max(...data.monthly_chart.map(m => Math.max(m.income, m.expense)), 1) : 1;

  return (
    <div className="space-y-4" data-testid="dashboard-home">
      {/* Karşılama */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border border-[#E2E8F0] px-4 md:px-5 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="bg-amber-50 text-amber-600 rounded-xl p-2.5"><Hand className="w-5 h-5" /></div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
              Hoş Geldiniz, {currentUser?.username || 'admin'}
            </h1>
            <p className="text-sm text-slate-500">Avrasya Sportif Faaliyetler Yönetim Paneli</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[12px] text-slate-500">{clock.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            <p className="text-[12px] text-slate-400">{clock.toLocaleDateString('tr-TR', { weekday: 'long' })}</p>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-slate-900 tabular-nums">
            {clock.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
          </p>
          <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="dashboard-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Hesap kartları — bu ayın hesap bazlı durumu + geçen aydan devir */}
      {accountCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="dashboard-account-cards">
          {accountCards.map(a => (
            <div key={a.key} className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4"
              data-testid={`dashboard-account-${a.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`rounded-xl p-2.5 ${a.type === 'cash' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-[#176B91]'}`}>
                    {a.type === 'cash' ? <Banknote className="w-5 h-5" /> : <Landmark className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold truncate">
                      {a.name} · {a.type === 'cash' ? 'Kasa' : 'Banka'}
                    </p>
                    <p className={`text-2xl font-bold tracking-tight tabular-nums ${a.month_net < 0 ? 'text-rose-700' : 'text-emerald-700'}`}
                      data-testid={`dashboard-account-net-${a.key}`}>
                      {a.month_net > 0 ? '+' : ''}{tl(a.month_net)}
                    </p>
                    <p className="text-[11px] text-slate-500">{MONTH_NAMES[new Date().getMonth()]} ayı net durumu</p>
                  </div>
                </div>
                <button onClick={() => onNavigate('cash-accounts')}
                  className="text-[11px] text-[#176B91] hover:underline shrink-0"
                  data-testid={`dashboard-account-link-${a.key}`}>Detay</button>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] px-2.5 py-2">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Giriş</p>
                  <p className="text-[13px] font-bold text-emerald-700 tabular-nums">{tl(a.month_income)}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] px-2.5 py-2">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Çıkış</p>
                  <p className="text-[13px] font-bold text-rose-700 tabular-nums">{tl(a.month_expense)}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] px-2.5 py-2">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Devir</p>
                  <p className={`text-[13px] font-bold tabular-nums ${a.prev_month_net < 0 ? 'text-rose-700' : 'text-slate-900'}`}
                    data-testid={`dashboard-account-prev-${a.key}`}>{tl(a.prev_month_net)}</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">
                Devir: {MONTH_NAMES[(new Date().getMonth() + 11) % 12]} ayından kalan net
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Faturalı ama tahsil edilmemiş */}
      {showFinance && invUnpaid?.count > 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="invoiced-unpaid-card">
          <button onClick={() => setShowInvUnpaid(v => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
            data-testid="invoiced-unpaid-toggle">
            <div className="flex items-center gap-3 min-w-0">
              <div className="rounded-xl p-2.5 bg-indigo-50 text-indigo-700"><FileText className="w-5 h-5" /></div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Faturalı Tahsil Edilmemiş (Tüm Zamanlar)</p>
                <p className="text-2xl font-bold text-indigo-700 tabular-nums" data-testid="invoiced-unpaid-total">{tl(invUnpaid.total)}</p>
                <p className="text-[11px] text-slate-500">
                  {invUnpaid.count} rezervasyon · {invUnpaid.customer_count} müşteri
                  {invUnpaid.overdue_count > 0 && <span className="text-rose-700 font-semibold"> · {invUnpaid.overdue_count} vadesi geçmiş ({tl(invUnpaid.overdue_total)})</span>}
                </p>
              </div>
            </div>
            <span className="text-[12px] text-[#176B91] shrink-0">{showInvUnpaid ? 'Gizle' : 'Müşterileri gör'}</span>
          </button>
          {showInvUnpaid && (
            <div className="border-t border-[#E2E8F0] max-h-[320px] overflow-y-auto" data-testid="invoiced-unpaid-list">
              {invUnpaid.items.map(it => (
                <div key={it.id}
                  className={`flex items-center justify-between gap-3 px-4 py-2 border-b border-[#F1F5F9] ${it.is_overdue ? 'bg-rose-50' : ''}`}
                  data-testid={`invoiced-unpaid-row-${it.id}`}>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-slate-800 truncate">{it.customer_name}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {trDate(it.date)} {(it.start_time || '').slice(0, 5)} · {it.salon_name}
                      {it.due_date && <span className={it.is_overdue ? 'text-rose-700 font-semibold' : ''}> · vade {trDate(it.due_date)}</span>}
                    </p>
                  </div>
                  <span className="text-[13px] font-bold text-slate-900 tabular-nums shrink-0">{tl(it.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bugünün hatırlatmaları — kişinin kendi yazdığı görevler */}
      {reminders.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4" data-testid="dashboard-reminders">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <BellRing className="w-4 h-4 text-amber-600" /> Bugünün Hatırlatmaları
            </p>
            <button onClick={() => onNavigate('reminders')} className="text-[11px] text-[#176B91] hover:underline inline-flex items-center gap-1"
              data-testid="dashboard-reminders-link">
              Tümü <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {reminders.slice(0, 6).map(r => (
              <div key={r.id} className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                data-testid={`dashboard-reminder-${r.id}`}>
                <span className="text-[12px] font-bold text-amber-800 tabular-nums shrink-0">{r.time}</span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-800 truncate">{r.title}</p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {r.type === 'weekly' ? 'Haftalık tekrar' : 'Tek seferlik'}
                    {!isPersonnel && r.personnel_name ? ` · ${r.personnel_name}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI */}
      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="border-[#E2E8F0]"><CardContent className="p-4">
              <div className="h-3 w-24 bg-slate-100 rounded animate-pulse mb-2" />
              <div className="h-6 w-28 bg-slate-100 rounded animate-pulse" />
            </CardContent></Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-kpis">
          {kpis.map(c => <KpiCard key={c.label} {...c} />)}
        </div>
      )}

      {/* Orta blok */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <Panel
          title={`Bugünkü Program — ${trDate(data?.today)}`} icon={Clock} testid="dashboard-today-program"
          action={<Button variant="ghost" size="sm" className="h-7 text-[11px] text-[#176B91]" onClick={() => onNavigate('calendar')} data-testid="dashboard-open-calendar">
            Tüm Takvimi Görüntüle <ArrowRight className="w-3 h-3 ml-1" />
          </Button>}
        >
          {!data ? <p className="text-sm text-slate-400 py-6 text-center">Yükleniyor...</p>
            : data.program.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center" data-testid="today-program-empty">Bugün için planlanmış program yok</p>
              : (
                <div className="space-y-2 max-h-[330px] overflow-y-auto pr-1">
                  {data.program.map(p => (
                    <div key={p.id} className={`rounded-lg border border-[#E2E8F0] border-l-[3px] px-3 py-2.5 ${KIND_STYLE[p.kind]}`} data-testid={`program-item-${p.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{p.title}</p>
                          <p className="text-[11px] text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.salon_name}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[12px] font-semibold text-slate-700 tabular-nums">{p.start_time} – {p.end_time}</p>
                          {p.kind === 'organization'
                            ? <Badge className="text-[10px] bg-sky-100 text-[#176B91] hover:bg-sky-100">ORGANİZASYON</Badge>
                            : showFinance ? <span className="text-[11px] text-slate-500">{tlShort(p.total_amount)}</span> : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </Panel>

        <Panel
          title="Yaklaşan Organizasyonlar" icon={CalendarRange} testid="dashboard-upcoming-orgs"
          action={<Button variant="ghost" size="sm" className="h-7 text-[11px] text-[#176B91]" onClick={() => onNavigate('organizations')} data-testid="dashboard-open-organizations">
            Tümünü Gör <ArrowRight className="w-3 h-3 ml-1" />
          </Button>}
        >
          {!data ? <p className="text-sm text-slate-400 py-6 text-center">Yükleniyor...</p>
            : data.upcoming_organizations.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center" data-testid="upcoming-orgs-empty">Yaklaşan organizasyon yok</p>
              : (
                <div className="space-y-2 max-h-[330px] overflow-y-auto pr-1">
                  {data.upcoming_organizations.map(o => (
                    <button key={o.organization_id} onClick={() => onNavigate('organizations')}
                      className="w-full flex items-center gap-3 rounded-lg border border-[#E2E8F0] px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
                      data-testid={`upcoming-org-${o.organization_id}`}>
                      <div className="text-center shrink-0 w-10">
                        <p className="text-base font-bold text-slate-900 leading-none">{o.date.slice(8, 10)}</p>
                        <p className="text-[10px] text-slate-500 uppercase">{new Date(o.date + 'T00:00:00').toLocaleDateString('tr-TR', { month: 'short' })}</p>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{o.name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{o.salon_name} · {o.start_time}–{o.end_time}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <Badge className={`text-[10px] ${o.status === 'aktif' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'} hover:bg-current/10`}>
                          {o.status === 'aktif' ? 'Aktif' : 'Planlandı'}
                        </Badge>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          {o.days_left === 0 ? 'Bugün' : o.days_left === 1 ? 'Yarın' : `${o.days_left} gün sonra`}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
        </Panel>

        <Panel title="Salon Doluluk Durumu" icon={MapPin} testid="dashboard-salon-occupancy"
          action={<span className="text-[11px] text-slate-500">{trDate(data?.today)}</span>}>
          {!data ? <p className="text-sm text-slate-400 py-6 text-center">Yükleniyor...</p>
            : (
              <div className="space-y-2.5 max-h-[330px] overflow-y-auto pr-1">
                {data.salon_occupancy.map(s => (
                  <div key={s.salon_id} data-testid={`salon-occupancy-${s.salon_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-slate-800 truncate">{s.name}</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {s.capacity ? `${s.capacity} kişi` : 'kapasite -'} · {s.reservation_count} rezervasyon
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[13px] font-bold text-slate-800">%{s.occupancy}</p>
                        <p className="text-[10px] text-slate-500 tabular-nums">
                          {s.first_start ? `${s.first_start}–${s.last_end}` : 'Müsait'}
                        </p>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                      <div className={`h-full rounded-full ${s.occupancy > 66 ? 'bg-emerald-500' : s.occupancy > 33 ? 'bg-[#176B91]' : s.occupancy > 0 ? 'bg-amber-500' : 'bg-slate-200'}`}
                        style={{ width: `${Math.max(s.occupancy, 2)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
        </Panel>
      </div>

      {/* Alt blok — yalnızca kasa/muhasebe yetkisi olanlara */}
      {showFinance && (
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
        <div className="xl:col-span-3">
          <Panel title="Son Muhasebe Hareketleri" icon={Wallet} testid="dashboard-recent-transactions"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] text-[#176B91]" onClick={() => onNavigate('financials')} data-testid="dashboard-open-financials">
              Tüm İşlemleri Gör <ArrowRight className="w-3 h-3 ml-1" />
            </Button>}>
            <div className="overflow-x-auto -mx-4 -my-4">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F8FAFC] text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Tarih</th>
                    <th className="text-left px-3 py-2 font-semibold">Tür</th>
                    <th className="text-left px-3 py-2 font-semibold">Açıklama</th>
                    <th className="text-right px-3 py-2 font-semibold">Tutar</th>
                    <th className="text-left px-4 py-2 font-semibold">Kasa/Banka</th>
                  </tr>
                </thead>
                <tbody>
                  {!data ? <tr><td colSpan={5} className="text-center py-8 text-slate-400">Yükleniyor...</td></tr>
                    : data.recent_transactions.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-slate-400">Kayıt yok</td></tr>
                      : data.recent_transactions.map((t, i) => (
                        <tr key={t.id || i} className={`border-t border-[#E2E8F0] ${i % 2 ? 'bg-[#F8FAFC]/60' : ''}`}>
                          <td className="px-4 py-2 whitespace-nowrap text-slate-600">{trDate(t.date)}</td>
                          <td className="px-3 py-2">
                            <Badge className={`text-[10px] ${t.type === 'income' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'} hover:bg-current/10`}>
                              {t.type === 'income' ? 'Gelir' : 'Gider'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 max-w-[260px] truncate text-slate-800">{t.title || '-'}</td>
                          <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${t.type === 'income' ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {t.type === 'income' ? '' : '- '}{tl(t.amount)}
                          </td>
                          <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{t.account_label}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <div className="xl:col-span-2">
          <Panel title={`Aylık Gelir / Gider Grafiği (${data?.year || ''})`} icon={TrendingUp} testid="dashboard-monthly-chart">
            {!data ? <p className="text-sm text-slate-400 py-10 text-center">Yükleniyor...</p> : (
              <ResponsiveContainer width="100%" height={248}>
                <BarChart data={data.monthly_chart} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748B' }} domain={[0, Math.ceil(chartMax / 50000) * 50000]}
                    tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                  <Tooltip formatter={(v) => tl(v)} contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#E2E8F0' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="income" name="Gelir" fill="#15803D" radius={[3, 3, 0, 0]} maxBarSize={14} />
                  <Bar dataKey="expense" name="Gider" fill="#DC2626" radius={[3, 3, 0, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>
      </div>
      )}

      {/* Alacak / hakem takip */}
      {!isPersonnel && showFinance && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Panel title="Borçlu Müşteriler (En Yüksek 5)" icon={Users} testid="dashboard-debtors"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] text-[#176B91]" onClick={() => onNavigate('customers')} data-testid="dashboard-open-customers">
              Müşteriler <ArrowRight className="w-3 h-3 ml-1" />
            </Button>}>
            {!data ? <p className="text-sm text-slate-400 py-6 text-center">Yükleniyor...</p>
              : data.debtors.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center" data-testid="debtors-empty">Borçlu müşteri yok</p>
                : (
                  <div className="divide-y divide-[#E2E8F0]">
                    {data.debtors.map(c => (
                      <button key={c.id} onClick={() => onNavigate('customers', { customerId: c.id })}
                        className="w-full flex items-center justify-between py-2 hover:bg-slate-50 px-1 rounded text-left"
                        data-testid={`debtor-row-${c.id}`}>
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 truncate">{c.name}</p>
                          <p className="text-[11px] text-slate-500">{c.phone || '-'}</p>
                        </div>
                        <span className="text-[13px] font-bold text-amber-700 shrink-0">{tl(c.balance)}</span>
                      </button>
                    ))}
                  </div>
                )}
          </Panel>

          <Panel title="Hakemden Bekleyen Tahsilat (Top 5)" icon={Clock} testid="dashboard-referee-pending"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] text-[#176B91]" onClick={() => onNavigate('referees')} data-testid="dashboard-open-referees">
              Hakemler <ArrowRight className="w-3 h-3 ml-1" />
            </Button>}>
            {refereePending.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center" data-testid="pending-referees-empty">Bekleyen tahsilat yok</p> : (
              <div className="divide-y divide-[#E2E8F0]">
                {refereePending.slice(0, 5).map(r => (
                  <button key={r.referee_id} onClick={() => onNavigate('referees', { refereeId: r.referee_id })}
                    className="w-full flex items-center justify-between py-2 hover:bg-slate-50 px-1 rounded text-left"
                    data-testid={`referee-pending-row-${r.referee_id}`}>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-slate-800 truncate">{r.name}</p>
                      <p className="text-[11px] text-slate-500">{r.to_collect_count || 0} maç bekliyor</p>
                    </div>
                    <span className="text-[13px] font-bold text-rose-700 shrink-0">{tl(r.to_collect)}</span>
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
